import { useState, useEffect } from "react";
import { ShieldCheck, Award, Users, Calendar, ChevronDown, ChevronUp } from "lucide-react";
import { staffMembers } from "../data";
import { fetchPageBySlug } from "../lib/cms";

export default function About() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedMembers, setExpandedMembers] = useState({});

  const toggleExpand = (name) => {
    setExpandedMembers((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  useEffect(() => {
    async function loadData() {
      try {
        const page = await fetchPageBySlug("om-os");
        setPageData(page);
      } catch (error) {
        console.error("Fejl ved indlæsning af om os data:", error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  return (
    <div className="bg-slate-50 text-slate-900 antialiased min-h-screen">
      {/* Header Banner */}
      <section className="relative overflow-hidden bg-slate-900 py-24 border-b border-slate-800">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://assets.auxodev.dk/assets/845935e5-3993-406d-896b-e0a4423b6917/5697a9ce-44f3-470d-b2c3-6db66fd67c1a.jpg"
            alt="Om os"
            className="h-full w-full object-cover filter brightness-30 contrast-105"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-transparent" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-left">
          <h1 className="text-2xl font-extrabold tracking-tight sm:text-4xl md:text-5xl text-white">
            {pageData?.title || "Om os"}
          </h1>
          <p className="mt-4 max-w-2xl text-sm md:text-base text-slate-300">
            Lær mere om vores historie, vores værdier og det stærke team af mekanikere, der holder dig kørende.
          </p>
        </div>
      </section>

      {/* History Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
            {/* History Text (Verbatim from Scrape) */}
            <div className="lg:col-span-7 text-left space-y-6">
              <div className="inline-flex items-center gap-2 rounded-full border border-blue-500/20 bg-blue-500/5 px-4 py-1.5 text-xs font-semibold tracking-wide text-blue-600">
                <Calendar className="h-4 w-4" />
                Etableret i 1976 i Skærbæk
              </div>
              <h2 className="text-xl font-bold text-slate-900 sm:text-3xl">
                Værkstedets Historie
              </h2>
              <div className="h-1 w-12 bg-blue-600 rounded-full" />

              {loading ? (
                <div className="space-y-4 animate-pulse">
                  <div className="h-4 bg-slate-200 rounded w-full" />
                  <div className="h-4 bg-slate-200 rounded w-5/6" />
                  <div className="h-4 bg-slate-200 rounded w-4/5" />
                </div>
              ) : (
                <div
                  className="prose prose-slate max-w-none text-slate-600 leading-relaxed space-y-6"
                  dangerouslySetInnerHTML={{ __html: pageData?.content || "" }}
                />
              )}
            </div>

            {/* Visual Stats */}
            <div className="lg:col-span-5 relative">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-blue-500/5 to-blue-500/5 blur-2xl" />
              <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 p-8 text-left space-y-8 shadow-sm">
                <h3 className="text-base font-bold text-slate-900 uppercase tracking-wider border-b border-slate-200 pb-3">
                  Vores Værdier
                </h3>

                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white text-blue-500 border border-slate-200 shadow-sm">
                      <ShieldCheck className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="text-base font-bold text-slate-900">Højeste Kvalitet</h4>
                      <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                        Vi går aldrig på kompromis med kvaliteten af vores arbejde eller de reservedele, vi monterer.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white text-blue-500 border border-slate-200 shadow-sm">
                      <Users className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="text-base font-bold text-slate-900">Lokal Forankring</h4>
                      <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                        Vi er stolte af vores lokale rødder i Skærbæk og støtter det lokale samfund og erhvervsliv.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white text-blue-500 border border-slate-200 shadow-sm">
                      <Award className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="text-base font-bold text-slate-900">Uafhængigt Værksted</h4>
                      <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                        Vi er ikke tilknyttet dyre kæder. Det sikrer dig personlig service og mere fordelagtige priser.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Staff Section */}
      <section className="py-20 bg-slate-50 border-t border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-3 mb-16">
            <p className="text-xs font-bold uppercase tracking-wider text-blue-600">
              Mød teamet
            </p>
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Vores Medarbejdere
            </h2>
            <div className="h-1 w-12 bg-blue-600 mx-auto rounded-full mt-4" />
          </div>

          {/* Staff Grid */}
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {staffMembers.map((member) => {
              const isLong = member.description.length > 100;
              const isExpanded = !!expandedMembers[member.name];
              const displayText = isLong && !isExpanded
                ? `${member.description.slice(0, 100)}...`
                : member.description;

              return (
                <div
                  key={member.name}
                  className="group flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition-all hover:border-blue-500/30 hover:shadow-md text-left"
                >
                  {/* Image */}
                  <div className="relative aspect-[4/5] overflow-hidden bg-slate-100 border-b border-slate-100">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-102"
                      loading="lazy"
                    />
                    {/* Subtle Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent" />
                    
                    {/* Name & Role on Image for premium feel */}
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-sm sm:text-base font-bold text-white">{member.name}</h3>
                      <p className="text-xs text-blue-400 font-medium mt-0.5">{member.role}</p>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="flex-1 p-5 flex flex-col justify-between bg-white">
                    <div className="space-y-3">
                      <p className="text-xs text-slate-600 leading-relaxed transition-all duration-300">
                        {displayText}
                      </p>
                      
                      {isLong && (
                        <button
                          onClick={() => toggleExpand(member.name)}
                          className="inline-flex items-center gap-1 text-xs font-bold text-blue-600 hover:text-blue-700 transition-colors focus:outline-none"
                        >
                          {isExpanded ? (
                            <>
                              Vis mindre <ChevronUp className="h-3.5 w-3.5" />
                            </>
                          ) : (
                            <>
                              Læs mere <ChevronDown className="h-3.5 w-3.5" />
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
